import { useAsync, useNavigateLogin } from "@/shared/hooks";
import { SunsApiError } from "@suns/api";
import {
  Card,
  Flex,
  SkeletonText,
  SkeletonHeading,
  Text,
  Container,
  Separator,
  Grid,
  Popover,
  PopoverContent,
  Command,
  CommandItem,
  CommandList,
  Input,
} from "@suns/design-system";
import { PopoverTrigger } from "@suns/design-system/src/components/Popover/Popover";
import {
  CommandEmpty,
  CommandGroup,
} from "@suns/design-system/src/components/Command/Command";
import { Loader2Icon, PencilIcon, SearchIcon } from "@suns/design-system/icons";
import { Page, PlayerHeaderSkeleton } from "@/components";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  formatAge,
  formatInches,
  formatMoney,
  formatWeight,
} from "@/shared/utils/helper-functions";
import { PlayerInfoSection } from "./components/PlayersInfoSection";
import { useEffect, useMemo, useState } from "react";
import { apolloApi } from "@/shared/api";
import { toast, ToastType } from "@/shared/utils/toaster";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { PlayerResponse } from "@suns/api/generated-client/apollo";
import { PlayerDetails } from "./components/PlayerDetails";
import { usePrompt } from "@/components/Prompt/Prompt";
import { viewPlayerLoader } from "./loaders/player-loader";
import countries from "./country-code.json";
import { CURRENT_SEASON_LABEL } from "@/shared/const";
import { AgentCacheItem, useAgentsMap } from "@/shared/hooks/useAgentsMap";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PlayerProfileHeader from "./components/PlayerProfileHeader";
import { getLabel } from "@/shared/utils";

export interface PlayerAgentInfo {
  agentId: number;
}

export function PlayersView() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const navigateLogin = useNavigateLogin();
  const prompt = usePrompt();

  const { response, loading, error } = useAsync(viewPlayerLoader, {
    playerId: Number(playerId),
  });

  const { response: agents, error: agentsError } = useAgentsMap();

  const [playerResponse, setPlayerResponse] = useState<PlayerResponse | null>(
    null
  );

  const [saving, setSaving] = useState(false);

  const handleClickUpsertAgent = async (agentId: number) => {
    const agent = agents?.get("agent-" + agentId.toString());
    const confirmation = await prompt(
      "Are you sure you want to update the agent?",
      `This will update the agent for this player from ${playerResponse?.player.agent?.name ?? response?.player.agent?.name} to ${agent?.fullName}.`
    );

    if (confirmation) {
      try {
        setSaving(true);
        const updatedAgent = await apolloApi.upsertPlayersAgent({
          id: Number(playerId),
          agentId: agentId,
        });

        setPlayerResponse(updatedAgent);

        toast(ToastType.SUCCESS, "Agent Updated");
      } catch (error) {
        if (error instanceof SunsApiError && error.authError) {
          toast(ToastType.ERROR, "Please log back in to update.");
          navigateLogin();
        }
        toast(ToastType.ERROR, "Unable to update agent. Please try again.");
      } finally {
        setSaving(false);
      }
    }
  };

  const hanldePhoneClick = (phone: string | undefined) => {
    if (phone) {
      window.open(`tel:${phone}`, "_blank");
    }
  };

  if (agentsError) {
    throw new SunsApiError("Error loading agents", { cause: agentsError });
  }

  useEffect(() => {
    setPlayerResponse(null);
  }, [playerId]);

  return (
    <Page
      title={
        response
          ? `${response?.player.firstName} ${response?.player.lastName}`
          : "View Player"
      }
      loading={loading}
      loadingState={<PlayerViewLoading />}
      onPrint={() => navigate(`/players/${playerId}/print`)}
      className="mt-3 lg:mt-1"
      size="sm"
      render={() => {
        if (error) {
          throw new SunsApiError("Error loading report", { cause: error });
        }

        const { player, scores } = response!;

        const playerCountry = player.homeCountryCode
          ? getCountryName(player.homeCountryCode ?? "")
          : player.homeCountryCode;

        const playerFreeAgentStatus = (() => {
          switch (true) {
            case !!player.metadata[0]?.playerOption &&
              !!player.metadata[0]?.restrictedFreeAgent:
              return `PO:${player.metadata[0]?.playerOption};RFA:${player.metadata[0]?.restrictedFreeAgent}`;
            case !!player.metadata[0]?.playerOption &&
              !!player.metadata[0]?.unrestrictedFreeAgent:
              return `PO:${player.metadata[0]?.playerOption};UFA:${player.metadata[0]?.unrestrictedFreeAgent}`;
            case !!player.metadata[0]?.playerOption:
              return `PO:${player.metadata[0]?.playerOption}`;
            case !!player.metadata[0]?.teamOption &&
              !!player.metadata[0]?.restrictedFreeAgent:
              return `TO:${player.metadata[0]?.teamOption};RFA:${player.metadata[0]?.restrictedFreeAgent}`;
            case !!player.metadata[0]?.teamOption &&
              !!player.metadata[0]?.unrestrictedFreeAgent:
              return `TO:${player.metadata[0]?.teamOption};UFA:${player.metadata[0]?.unrestrictedFreeAgent}`;
            case !!player.metadata[0]?.teamOption:
              return `TO:${player.metadata[0]?.teamOption}`;
            case !!player.metadata[0]?.unrestrictedFreeAgent:
              return `UFA:${player.metadata[0]?.unrestrictedFreeAgent}`;
            case !!player.metadata[0]?.restrictedFreeAgent:
              return `RFA:${player.metadata[0]?.restrictedFreeAgent}`;
            default:
              return null;
          }
        })();

        const agentNumber =
          playerResponse?.player.agent?.mobilePhone || player.agent?.mobilePhone
            ? parsePhoneNumberFromString(
                playerResponse?.player.agent?.mobilePhone ??
                  player.agent?.mobilePhone ??
                  ""
              )?.format("INTERNATIONAL")
            : parsePhoneNumberFromString(
                playerResponse?.player.agent?.workPhone ??
                  player.agent?.workPhone ??
                  ""
              )?.format("INTERNATIONAL");

        return (
          <Container size="xl">
            <Flex direction="down" gap="md">
              <Card className="overflow-hidden px-0 pb-4 pt-2">
                <PlayerProfileHeader player={player} />
                <Separator className="h-[2px] bg-gray-200" />
                <Grid
                  gap={"md"}
                  columns={["md:3"]}
                  className="z-1 relative bg-white px-4 pt-4"
                >
                  <PlayerDetails
                    title="Player Info"
                    items={[
                      Boolean(player.birthDate) && {
                        label: "Birthdate",
                        value: `${dayjs(player.birthDate).format("MM/DD/YYYY")} (${formatAge(player.birthDate ?? "")})`,
                      },
                      Boolean(player.homeTown || player.homeState) && {
                        label: "Birthplace",
                        value: `${player.homeTown ? player.homeTown : "--"}${
                          player.homeState ? ", " + player.homeState : ""
                        }`,
                      },
                      Boolean(player.collegeTeam?.fullName) && {
                        label: "Pre-Draft Team",
                        value: player.collegeTeam?.fullName,
                      },
                      Boolean(player.highschool) && {
                        label: "High School",
                        value: player.highschool,
                      },
                      Boolean(playerCountry) && {
                        label: "Nationality",
                        value: playerCountry,
                      },
                      Boolean(typeof player.yearsOfService == "number") && {
                        label: "Years of Service",
                        value:
                          player.yearsOfService === 0
                            ? "Rookie"
                            : `${player.yearsOfService} years`,
                      },
                    ]}
                  />
                  <PlayerDetails
                    title="Status"
                    items={[
                      Boolean(player.level) && {
                        label: "Level",
                        value: getLabel(player.level),
                      },
                      Boolean(player.metadata[0]?.capHit) && {
                        label: "Contract Status",
                        value: player.metadata[0]?.capHit
                          ? "Under Contract"
                          : "Free Agent",
                      },
                      Boolean(player.metadata[0]?.capHit) && {
                        label: `${CURRENT_SEASON_LABEL} Cap Hit`,
                        value: formatMoney(player.metadata[0]?.capHit),
                      },
                      Boolean(player.metadata[0]?.ppv) && {
                        label: "PPV",
                        value: formatMoney(player.metadata[0]?.ppv),
                      },
                      Boolean(typeof player.metadata[0]?.pmv == "number") && {
                        label: "PMV",
                        value:
                          player.metadata[0]?.pmv == 0
                            ? "Two-way"
                            : formatMoney(player.metadata[0]?.pmv),
                      },
                      Boolean(
                        typeof player.metadata[0]?.lowPmv == "number" &&
                          typeof player.metadata[0]?.highPmv == "number"
                      ) && {
                        label: "PMV Range",
                        value: `${formatMoney(player.metadata[0]?.lowPmv)} - ${formatMoney(player.metadata[0]?.highPmv)}`,
                      },
                      Boolean(playerFreeAgentStatus) && {
                        label: "Free Agency",
                        value: playerFreeAgentStatus,
                      },
                      {
                        label: "Agent",
                        value: (
                          <AgentSelector
                            value={
                              playerResponse?.player.agent?.name ??
                              player.agent?.name ??
                              "--"
                            }
                            options={Array.from(agents?.values() ?? [])}
                            onClick={() =>
                              navigate(
                                `/players/agents/${playerResponse?.player.agent?.id ?? player.agent?.id}`
                              )
                            }
                            onUpdate={handleClickUpsertAgent}
                            saving={saving}
                          />
                        ),
                      },
                      Boolean(agentNumber) && {
                        label: "Agent Number",
                        value: (
                          <Text
                            size="sm"
                            heading
                            className="cursor-pointer text-primary"
                            onClick={() => hanldePhoneClick(agentNumber)}
                          >
                            {agentNumber}
                          </Text>
                        ),
                      },
                    ]}
                  />

                  <PlayerDetails
                    title="Measurements"
                    items={[
                      Boolean(player.height) && {
                        label: "Height",
                        value: formatInches(
                          ((height) => {
                            return height;
                          })(player.height!)
                        ),
                      },
                      Boolean(player.weight) && {
                        label: "Weight",
                        value: formatWeight(player.weight),
                      },
                      Boolean(player.wing) && {
                        label: "Wing",
                        value: formatInches(player.wing!),
                      },
                      Boolean(player.reach) && {
                        label: "Reach",
                        value: formatInches(player.reach!),
                      },
                    ]}
                  />
                </Grid>
              </Card>
              <Card>
                <PlayerInfoSection
                  player={{
                    ...player,
                    scores: scores ?? [],
                  }}
                />
              </Card>
            </Flex>
          </Container>
        );
      }}
    />
  );
}

interface AgentSelectorProps {
  value?: string;
  options?: AgentCacheItem[];
  onClick?: () => void;
  onUpdate?: (value: number) => void;
  saving?: boolean;
}

function AgentSelector({
  value,
  options,
  onUpdate,
  onClick,
  saving,
}: AgentSelectorProps) {
  const [editValue, setEditValue] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const filteredOptions = useMemo(() => {
    return options?.filter((option) =>
      option.display.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  useEffect(() => {
    if (!editValue) {
      setSearchValue("");
    }
  }, [editValue]);

  return (
    <Flex gap="sm" align="center">
      <Text
        size="sm"
        heading
        className={value !== "--" ? "cursor-pointer text-primary" : ""}
        onClick={onClick}
      >
        {value}
      </Text>
      <Popover open={editValue} onOpenChange={setEditValue}>
        <PopoverTrigger asChild>
          <span className="cursor-pointer" onClick={() => setEditValue(true)}>
            {saving ? (
              <Loader2Icon className="size-4 animate-spin text-primary" />
            ) : (
              <PencilIcon className="size-4 pb-0.5 text-gray-500" />
            )}
          </span>
        </PopoverTrigger>
        <PopoverContent className="relative -left-16 w-[250px] p-0">
          <Command>
            <Flex className="border-b px-3" align="center" gap="sm">
              <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <Input
                className="h-11 w-full border-none p-0 text-[16px] font-normal
                  placeholder:text-muted-foreground"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </Flex>
            <CommandGroup>
              <CommandList>
                <CommandEmpty>No agents found</CommandEmpty>
                {filteredOptions?.map((option) => (
                  <CommandItem
                    className="font-normal"
                    key={option.id}
                    onSelect={() => {
                      onUpdate?.(option.id);
                      setEditValue(false);
                    }}
                  >
                    {option.fullName}
                  </CommandItem>
                ))}
              </CommandList>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </Flex>
  );
}

function PlayerViewLoading() {
  return (
    <Container size="xl">
      <Flex direction="down" gap="md">
        <Card>
          <PlayerHeaderSkeleton />
        </Card>
        <Card>
          <Flex className="w-full max-w-4xl" direction="down" gap="lg">
            <Flex direction="down" gap="md">
              <SkeletonHeading size="3xl" className="w-36" />
              <SkeletonHeading size="3xl" className="w-28" />
            </Flex>
            <SkeletonText rows={2} />
            <SkeletonText rows={3} />
          </Flex>
        </Card>
      </Flex>
    </Container>
  );
}

function getCountryName(code: string) {
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  const countryCodesMap = Object.fromEntries(
    countries.map((c) => [c["alpha-3"], c["alpha-2"]])
  );
  const twoLetterCode = countryCodesMap[code] || code;
  try {
    return regionNames.of(twoLetterCode);
  } catch {
    return null;
  }
}
